import React, { useState, useEffect, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, Input, Empty, List, Button, Popconfirm, message, Space } from 'antd';
import { ExclamationCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { debounce } from 'lodash';
import './index.less';

const defaultLabelCol = {
  labelCol: {
    flex: '80px',
    style: {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      width: '100%',
    },
  },
  wrapperCol: { flex: 1 },
};

const ListAddErpModal = (props, ref) => {
  const {
    /* *** 中间层卡控属性 *** */
    visible,
    onMidwareModalOk,
    onMidwareModalCancel,
    onMidwareOperateDeleteItem,
    onMidwareOperateTranslateOwnerItem,

    /* *** 顶层透传下来的属性 *** */
    title = '新增人员',
    extraTitle,
    extraDis,
    inputTitle = '添加成员',
    listTitle = (len) => `成员管理（${len}）`,
    extraForm = { extraFormInstance: null, renderExtraForm: null }, //拓展区域的form
    fetchErpList,
    renderExtraBtns,
    footer = null,
    fieldCtrlDelete = 'canDelete',
    fieldCtrlTransfer = 'canTransfer',
    ...othersProps
  } = props;
  const { extraFormInstance = null, renderExtraForm = null } = extraForm;
  const [modalForm] = Form.useForm();
  const [addErpUserRes, setAddErpUserRes] = useState(null); //添加api的返回值
  const [inlineErpList, setInlineErpList] = useState([]);

  const memoizedFetchErpList = useCallback(() => {
    let didMount = true;
    fetchErpList?.()
      .then((erpList) => {
        if (!didMount) return;
        setInlineErpList(erpList);
      })
      .catch((ex) => {
        message.error(ex?.message ?? '网络异常请稍后重试');
      });

    return () => {
      didMount = false;
    };
  }, [fetchErpList]);

  /* 初始化获取erplist */
  useEffect(() => {
    memoizedFetchErpList();
  }, [memoizedFetchErpList]);

  /* ************************************************************************************
   *                                  【【 抛出实例方法 】】                                *
   ************************************************************************************* */
  useImperativeHandle(ref, () => ({
    handleInlineRefreshList: () => {
      onHandleInlineRefreshList();
    },
  }));
  /* 抛出实例方法 - 【1】主动更新列表数据 */
  function onHandleInlineRefreshList() {
    if (!!memoizedFetchErpList && !!fetchErpList) {
      memoizedFetchErpList();
    }
  }

  /* ************************************************************************************
   *                                  【【 响应添加成果 】】                                *
   ************************************************************************************* */
  /* ***** ！！！！【注意一定是ret==='0'时的数据，ret!=='0'业务方自己报错】！！！！ ***** */
  const memoizedAddErpRes = useMemo(() => {
    if (!addErpUserRes) return null;
    const { success = null, failure = null, reasons = null } = addErpUserRes;
    if (!success && !failure) return null;

    if (success && success > 0 && (!failure || (failure && failure === 0))) {
      // 只有成功的
      message.success('添加成功！');
    } else if (failure && failure > 0 && (!success || (success && success === 0))) {
      // 只有失败的
      return (
        <div className="ty-list-add-erp-res-fa">
          <div className="add-erp-res-sum">
            <span className="add-erp-res-sum-icon">
              <CloseCircleFilled className="error-icon sum-icon" />
            </span>
            <span className="add-erp-res-sum-txt">添加失败{failure}个</span>
          </div>
          <div className="add-erp-error-list-fa">
            {reasons && Array.isArray(reasons) && reasons.length > 0
              ? reasons.map((item, index) => {
                  return (
                    <div className="add-erp-error-item" key={index}>
                      {item?.reason && <span className="add-erp-error-reason">{item?.reason}：</span>}
                      <span className="add-erp-error-erps">{item?.erps || ''}</span>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      );
    } else {
      // 成功和失败都有的
      return (
        <div className="ty-list-add-erp-res-fa">
          <div className="add-erp-res-sum">
            <span className="add-erp-res-sum-icon">
              <ExclamationCircleFilled className="warning-icon sum-icon" />
            </span>
            <span className="add-erp-res-sum-txt">
              添加成功{success || '0'}个，添加失败{failure || '0'}个
            </span>
          </div>
          <div className="add-erp-error-list-fa">
            {reasons && Array.isArray(reasons) && reasons?.length > 0
              ? reasons.map((item, index) => {
                  return (
                    <div className="add-erp-error-item" key={index}>
                      {item?.reason && <span className="add-erp-error-reason">{item?.reason}：</span>}
                      <span className="add-erp-error-erps">{item?.erps || '-'}</span>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      );
    }
  }, [addErpUserRes]);

  /* ************************************************************************************
   *                                  【【 回抛事件 】】                                   *
   ************************************************************************************* */
  /* ********************* 【action】提交表单，添加erp ********************* */
  async function onSubmit(values) {
    // 1、默认form的值
    try {
      const defaulRres = await modalForm.validateFields();
      let erpsStr = defaulRres?.erps || '';
      erpsStr = erpsStr && typeof erpsStr === 'string' && erpsStr?.trim();
      erpsStr = erpsStr?.replace(/，/gi, ','); //替换所有的中文逗号为英文逗号
      erpsStr = erpsStr?.replace(/[\r\n]/g, ','); //替换所有的换行符为英文逗号
      erpsStr = erpsStr?.replace(/\s+/g, ','); //替换所有的空格符为英文逗号
      // 去掉为空的元素
      erpsStr = erpsStr
        ?.split(',')
        ?.filter((item) => (item ?? '') !== '')
        ?.join(',');
      // 重新赋值
      defaulRres.erps = erpsStr;

      // 2、拓展区form的值
      let extraRes = {};
      if (extraFormInstance) {
        extraRes = await extraFormInstance.validateFields();
      }
      // 3、合并输出
      const contactFormValues = { ...defaulRres, ...extraRes };
      onMidwareModalOk &&
        typeof onMidwareModalOk === 'function' &&
        onMidwareModalOk(contactFormValues, successCallBack);

      // 4、点击添加按钮后 不管成功与否 清空输入框里面的内容后,重置Form
      modalForm && modalForm.resetFields();
      extraFormInstance && extraFormInstance.resetFields();
    } catch (e) {
      console.error(e);
    }
  }

  /* 业务放接口调用成功以后的回调数据【根据回调数据绝对渲染的UI】 */
  function successCallBack(successBackData) {
    setAddErpUserRes(successBackData);
    refreshOnBtnSuccess();
  }

  /* ********************* 【action】点击删除按钮回调 ********************* */
  function handleInlineDeleteItem(item) {
    onMidwareOperateDeleteItem &&
      typeof onMidwareOperateDeleteItem === 'function' &&
      onMidwareOperateDeleteItem(item, refreshOnBtnSuccess);
  }

  /* ********************* 【action】点击权限转移按钮回调 ********************* */
  function handleInlineTranstlateOwner(item) {
    onMidwareOperateTranslateOwnerItem &&
      typeof onMidwareOperateTranslateOwnerItem === 'function' &&
      onMidwareOperateTranslateOwnerItem(item, refreshOnBtnSuccess);
  }

  /* ********************* 【action】刷新列表 ********************* */
  function refreshOnBtnSuccess() {
    if (!!memoizedFetchErpList && !!fetchErpList) {
      memoizedFetchErpList();
    }
  }

  /* ********************* 【action】点击modal的cancel回调 ********************* */
  function handleInlineCancel(e) {
    onMidwareModalCancel && typeof onMidwareModalCancel === 'function' && onMidwareModalCancel(e);
    modalForm && modalForm.resetFields();
    extraFormInstance && extraFormInstance.resetFields();
    setAddErpUserRes(null);
    setInlineErpList([]);
  }

  /* ********************* 【action】handleInlineAfterClose,防止外围主动调用close时没有清空数据 ********************* */
  function handleInlineAfterClose() {
    modalForm && modalForm.resetFields();
    extraFormInstance && extraFormInstance.resetFields();
    setAddErpUserRes(null);
    setInlineErpList([]);
  }

  return (
    <Modal
      okText="确认"
      cancelText="取消"
      className="ty-list-add-erp-modal"
      width={800}
      bodyStyle={{ paddingTop: '16px', paddingBottom: '16px', maxHeight: '622px', overflow: 'hidden auto' }}
      title={title}
      footer={footer}
      visible={visible}
      onCancel={debounce(handleInlineCancel, 200)}
      afterClose={handleInlineAfterClose}
    >
      {/* 拓展标题区 */}
      {extraTitle || extraDis ? (
        <div className="ty-list-extra-content-fa">
          <div className="extra-title-fa">{extraTitle}</div>
          <div className="extra-extraDis-fa">{extraDis}</div>
        </div>
      ) : null}

      <div className="ty-list-add-erp-fa">
        {/* 左侧form区域 */}
        <div className="erp-form-fa">
          <h3 className="erp-form-title">{inputTitle}</h3>
          <div className="erp-default-form-fa">
            <Form onFinish={onSubmit} form={modalForm} name="defaultForm" {...defaultLabelCol}>
              <Form.Item
                name="erps"
                label={
                  <span>
                    <span style={{ color: '#ff4d4f', marginRight: '2px' }}>*</span>
                    erp账号
                  </span>
                }
                style={{ marginBottom: '0px' }}
              >
                <Input.TextArea placeholder="输入多个ERP账号请以英文逗号或者空格区分" style={{ height: 98 }} />
              </Form.Item>
            </Form>
            {memoizedAddErpRes}
          </div>
          {/* 拓展区域的form,传入及显示 */}
          {renderExtraForm && typeof renderExtraForm === 'function' && (
            <div className="erp-extra-form-fa">{renderExtraForm(defaultLabelCol)}</div>
          )}
          <div className="erp-extra-form-submit-btn">
            <Button type="primary" shape="round" onClick={debounce(() => modalForm.submit(), 200)}>
              添加
            </Button>
          </div>
        </div>

        {/* 右侧list区域 */}
        <div className="erp-list-fa">
          <h3 className="erp-list-title">
            {listTitle(
              inlineErpList && Array.isArray(inlineErpList) && inlineErpList.length > 0 ? inlineErpList.length : '0'
            )}
          </h3>
          <>{inlineRenderErpList()}</>
        </div>
      </div>
    </Modal>
  );

  /* ********************************************
   * render-props,走内部,暂时关闭外部传递自定义的功能 *
   ********************************************* */
  function inlineRenderErpList() {
    if (inlineErpList && Array.isArray(inlineErpList) && inlineErpList.length > 0) {
      return (
        <List
          className="erp-loadmore-list"
          itemLayout="horizontal"
          dataSource={inlineErpList}
          renderItem={(item) => (
            <List.Item
              className="erp-loadmore-list-item"
              extra={
                <Space size={10} direction="horizontal" align="center">
                  {/* 拓展按钮放在最前面 */}
                  {renderExtraBtns && renderExtraBtns(item)}

                  {/* 转移组长权限的按钮【统一为：存在且可转移】 */}
                  {!(item[fieldCtrlTransfer] == null) && item[fieldCtrlTransfer] ? (
                    <Popconfirm
                      key="list-item-operation"
                      okButtonProps={{ shape: 'round' }}
                      cancelButtonProps={{ shape: 'round' }}
                      title="确认转移组长权限？"
                      onConfirm={debounce(() => handleInlineTranstlateOwner(item), 200)}
                    >
                      <a>转移组长权限</a>
                    </Popconfirm>
                  ) : null}

                  {/* 删除按钮【统一为：不存在或者可删除】 */}
                  {item[fieldCtrlDelete] == null || item[fieldCtrlDelete] ? (
                    <Popconfirm
                      key="list-item-operation"
                      okButtonProps={{ shape: 'round' }}
                      cancelButtonProps={{ shape: 'round' }}
                      title="确认删除？"
                      onConfirm={debounce(() => handleInlineDeleteItem(item), 200)}
                    >
                      <a>删除</a>
                    </Popconfirm>
                  ) : null}
                </Space>
              }
            >
              {item?.erp && item?.erpRealName ? (
                <div className="user-info">
                  <div className="user-name">{item?.erpRealName}</div>
                  <div className="user-erp">{item?.erp}</div>
                </div>
              ) : (
                '-'
              )}
            </List.Item>
          )}
        />
      );
    }

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'请先添加erp'} />;
  }
};

export default forwardRef(ListAddErpModal);
